/**
 * Drawing for the deal, before the first hand (Scott, 3 Nov 2026): everyone draws a card from the bones;
 * the lowest card deals first, and players sit around the table from the dealer in order of their cards,
 * lowest to highest. Equal cards (two 9s) are put in a random order — which also settles a tie for lowest.
 * Ranks only: 2 is lowest, Ace highest; suits don't count.
 */
import type { Card } from "./cards.ts";
import type { Rng } from "./rng.ts";

/** Seats in table order starting with the dealer: lowest card first, ties in random order. */
export function drawOrder(cards: Card[], rng: Rng): number[] {
  const tieBreak = cards.map(() => rng.next());
  return cards.map((_, i) => i).sort((a, b) => cards[a].rank - cards[b].rank || tieBreak[a] - tieBreak[b]);
}

/**
 * Seat everyone by the draw, keeping one player (`keep`, e.g. you on your own screen) in seat 0: returns the
 * new seat order (new seat → old seat) and the first dealer's new seat. Going round the table from the dealer
 * the cards then run lowest to highest.
 */
export function seatByDraw(cards: Card[], rng: Rng, keep = 0): { seats: number[]; dealer: number; order: number[] } {
  const order = drawOrder(cards, rng);              // old seats, dealer first
  const at = order.indexOf(keep);
  const seats = [...order.slice(at), ...order.slice(0, at)];
  return { seats, dealer: (order.length - at) % order.length, order };
}
